// src/components/Modal.tsx
import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
}: ModalProps) {
  // SSR-safe: don't try to portal if document is not available
  const canPortal = typeof document !== "undefined";

  useEffect(() => {
    if (!isOpen) return;

    // lock body scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // close on Escape
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        // close only when clicked on the overlay (not when clicking modal content)
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* modal card */}
      <div className="relative z-10 bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] mx-4 md:mx-0  flex flex-col overflow-hidden">
        <div className=" light-bg-color p-5 border-b border-gray-200">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 cursor-pointer"
            aria-label="Close"
          >
            <Icon icon="jam:close" width={30} height={30} />
          </button>

          {title && <h2 className="text-xl font-bold text-color ">{title}</h2>}
        </div>

        <div className="p-5  overflow-y-auto scrollbar-thin-gray">
          {children}
        </div>
      </div>
    </div>
  );

  return canPortal ? ReactDOM.createPortal(modal, document.body) : modal;
}
